/**
 * ScheduleFormDialog Component
 *
 * Create/edit dialog for unified schedules. All schedules use cron expressions
 * (no more once/recurring tabs). Conditional sections for workflow vs system_task.
 *
 * Workflow section reuses shared components from RunWorkflowDialog:
 * - WorkflowTargetSelector (radio buttons for target type + user selector)
 * - UserFilterOptionsForm (filter conditions for all_valid_users)
 * - RuntimeOptionsForm (memory protection toggles)
 */

import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Calendar, X, Loader2, Workflow, Settings } from 'lucide-react';
import { useCreateSchedule, useUpdateSchedule } from '../hooks/useSchedules';
import { imageWorkflowsApi } from '@/lib/api/imageWorkflows';
import { WorkflowTargetSelector } from '@/components/common/WorkflowTargetSelector';
import { UserFilterOptionsForm } from '@/components/common/UserFilterOptionsForm';
import { RuntimeOptionsForm } from '@/components/common/RuntimeOptionsForm';
import { useAuthStore } from '@/stores/authStore';
import type {
  UnifiedScheduleResponse,
  UnifiedScheduleType,
  CreateUnifiedScheduleRequest,
  UpdateUnifiedScheduleRequest,
} from '@/types/schedule';
import { UNIFIED_SCHEDULE_TYPE_LABELS } from '@/types/schedule';
import type { WorkflowTarget } from '@/types/users';
import type { RuntimeOptions, UserFilterOptions } from '@/types/imageWorkflow';

const SYSTEM_TASK_TYPE_OPTIONS = [
  { value: 'artifact_cleanup', label: '产物清理 (artifact_cleanup)' },
];

interface ScheduleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule?: UnifiedScheduleResponse | null;
  onSuccess?: () => void;
}

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Shanghai', label: '亚洲/上海' },
  { value: 'Asia/Tokyo', label: '亚洲/东京' },
  { value: 'America/New_York', label: '美国/纽约' },
  { value: 'Europe/London', label: '欧洲/伦敦' },
  { value: 'UTC', label: 'UTC' },
];

const CRON_PRESETS = [
  { value: '0 10 * * *', label: '每天 10:00' },
  { value: '0 8 * * 1-5', label: '工作日 08:00' },
  { value: '0 10 * * 1-5', label: '工作日 10:00' },
  { value: '0 9 * * 1', label: '每周一 09:00' },
  { value: '0 0 1 * *', label: '每月1日 00:00' },
  { value: '0 3 * * *', label: '每天 03:00 (夜间)' },
  { value: '*/30 * * * *', label: '每 30 分钟' },
  { value: '0 */6 * * *', label: '每 6 小时' },
];

const DEFAULT_USER_FILTER: UserFilterOptions = {
  exclude_test: true,
  has_self_birth_chart: false,
};

const DEFAULT_RUNTIME_OPTIONS: RuntimeOptions = {
  disable_memory_tool: true,
  disable_memory_write: true,
};

export function ScheduleFormDialog({
  open,
  onOpenChange,
  schedule,
  onSuccess,
}: ScheduleFormDialogProps) {
  const isEditing = !!schedule;
  const user = useAuthStore((state) => state.user);

  // Mutations
  const createSchedule = useCreateSchedule();
  const updateSchedule = useUpdateSchedule();

  // --- Form state ---
  const [scheduleType, setScheduleType] = useState<UnifiedScheduleType>('system_task');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [cronExpression, setCronExpression] = useState('0 10 * * 1-5');
  const [timezone, setTimezone] = useState('Asia/Shanghai');

  // Workflow-specific (using shared component state types)
  const [workflowId, setWorkflowId] = useState('');
  const [target, setTarget] = useState<WorkflowTarget>({
    type: 'current_user',
    user_ids: [],
  });
  const [userFilter, setUserFilter] = useState<UserFilterOptions>(DEFAULT_USER_FILTER);
  const [runtimeOptions, setRuntimeOptions] = useState<RuntimeOptions>(DEFAULT_RUNTIME_OPTIONS);

  // System task-specific
  const [taskType, setTaskType] = useState('');
  const [taskConfig, setTaskConfig] = useState('{}');

  // Fetch workflows for selector (only when workflow type is selected)
  const { data: workflowsData } = useQuery({
    queryKey: ['image-workflows-list'],
    queryFn: () => imageWorkflowsApi.list({ limit: 100 }),
    enabled: open && scheduleType === 'workflow',
    staleTime: 30_000,
  });
  const workflowOptions = (workflowsData?.workflows ?? []).map((w) => ({
    value: w.id,
    label: w.name,
  }));

  // Lock body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Initialize form from schedule when editing
  useEffect(() => {
    if (schedule) {
      setScheduleType(schedule.schedule_type);
      setName(schedule.name);
      setDescription(schedule.description || '');
      setCronExpression(schedule.cron_expression);
      setTimezone(schedule.timezone);

      if (schedule.workflow_config) {
        setWorkflowId(schedule.workflow_config.workflow_id);
        setTarget({
          type: schedule.workflow_config.target_type || 'current_user',
          user_ids: schedule.workflow_config.target_user_ids || [],
        });
        setUserFilter(
          schedule.workflow_config.user_filter
            ? { ...DEFAULT_USER_FILTER, ...schedule.workflow_config.user_filter }
            : DEFAULT_USER_FILTER
        );
        setRuntimeOptions(
          schedule.workflow_config.runtime_options
            ? { ...DEFAULT_RUNTIME_OPTIONS, ...schedule.workflow_config.runtime_options }
            : DEFAULT_RUNTIME_OPTIONS
        );
      }

      if (schedule.system_task_config) {
        setTaskType(schedule.system_task_config.task_type);
        setTaskConfig(JSON.stringify(schedule.system_task_config.config || {}, null, 2));
      }
    } else {
      resetForm();
    }
  }, [schedule, open]);

  const resetForm = () => {
    setScheduleType('system_task');
    setName('');
    setDescription('');
    setCronExpression('0 10 * * 1-5');
    setTimezone('Asia/Shanghai');
    setWorkflowId('');
    setTarget({ type: 'current_user', user_ids: [] });
    setUserFilter(DEFAULT_USER_FILTER);
    setRuntimeOptions(DEFAULT_RUNTIME_OPTIONS);
    setTaskType('');
    setTaskConfig('{}');
  };

  // Validation
  const isFormValid = (): boolean => {
    if (!name.trim()) return false;
    if (!cronExpression.trim()) return false;

    if (scheduleType === 'workflow') {
      if (!workflowId.trim()) return false;
      if (target.type === 'specific_users' && target.user_ids.length === 0) return false;
    } else {
      if (!taskType.trim()) return false;
    }

    return true;
  };

  // Build workflow config for submission
  const buildWorkflowConfig = () => ({
    workflow_id: workflowId,
    target_type: target.type,
    target_user_ids: target.type === 'specific_users' ? target.user_ids : null,
    user_filter: target.type === 'all_valid_users' ? userFilter : null,
    runtime_options: runtimeOptions,
  });

  // Build system task config for submission
  const buildSystemTaskConfig = () => {
    let parsedConfig: Record<string, unknown> = {};
    try {
      parsedConfig = JSON.parse(taskConfig);
    } catch {
      // Use empty object if JSON invalid
    }
    return {
      task_type: taskType,
      config: parsedConfig,
    };
  };

  // Submit
  const handleSubmit = useCallback(async () => {
    if (!isFormValid()) return;

    if (isEditing && schedule) {
      const data: UpdateUnifiedScheduleRequest = {
        name,
        description: description || null,
        cron_expression: cronExpression,
        timezone,
      };

      if (scheduleType === 'workflow') {
        data.workflow_config = buildWorkflowConfig();
      } else {
        data.system_task_config = buildSystemTaskConfig();
      }

      try {
        await updateSchedule.mutateAsync({ id: schedule.id, data });
        onOpenChange(false);
        onSuccess?.();
      } catch {
        // Error handled by hook
      }
    } else {
      const data: CreateUnifiedScheduleRequest = {
        name,
        description: description || null,
        schedule_type: scheduleType,
        cron_expression: cronExpression,
        timezone,
        is_enabled: true,
      };

      if (scheduleType === 'workflow') {
        data.workflow_config = buildWorkflowConfig();
      } else {
        data.system_task_config = buildSystemTaskConfig();
      }

      try {
        await createSchedule.mutateAsync(data);
        onOpenChange(false);
        onSuccess?.();
      } catch {
        // Error handled by hook
      }
    }
  }, [
    name,
    description,
    scheduleType,
    cronExpression,
    timezone,
    workflowId,
    target,
    userFilter,
    runtimeOptions,
    taskType,
    taskConfig,
    isEditing,
    schedule,
    createSchedule,
    updateSchedule,
    onOpenChange,
    onSuccess,
  ]);

  const isSubmitting = createSchedule.isPending || updateSchedule.isPending;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={() => onOpenChange(false)} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? '编辑定时任务' : '创建定时任务'}
            </h2>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1 space-y-6">
          {/* Schedule type selector (only for create) */}
          {!isEditing && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">任务类别 *</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setScheduleType('workflow')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    scheduleType === 'workflow'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Workflow
                      className={`h-5 w-5 ${
                        scheduleType === 'workflow' ? 'text-blue-600' : 'text-gray-400'
                      }`}
                    />
                    <span
                      className={`font-medium ${
                        scheduleType === 'workflow' ? 'text-blue-900' : 'text-gray-900'
                      }`}
                    >
                      {UNIFIED_SCHEDULE_TYPE_LABELS.workflow}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">定时执行图片工作流</p>
                </button>
                <button
                  type="button"
                  onClick={() => setScheduleType('system_task')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    scheduleType === 'system_task'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Settings
                      className={`h-5 w-5 ${
                        scheduleType === 'system_task' ? 'text-blue-600' : 'text-gray-400'
                      }`}
                    />
                    <span
                      className={`font-medium ${
                        scheduleType === 'system_task' ? 'text-blue-900' : 'text-gray-900'
                      }`}
                    >
                      {UNIFIED_SCHEDULE_TYPE_LABELS.system_task}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">定时执行系统维护任务</p>
                </button>
              </div>
            </div>
          )}

          {/* Editing: show locked type badge */}
          {isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">任务类别</label>
              <div className="text-sm text-gray-500 flex items-center gap-2">
                {scheduleType === 'workflow' ? (
                  <>
                    <Workflow className="h-4 w-4 text-blue-600" />
                    {UNIFIED_SCHEDULE_TYPE_LABELS.workflow}
                  </>
                ) : (
                  <>
                    <Settings className="h-4 w-4 text-purple-600" />
                    {UNIFIED_SCHEDULE_TYPE_LABELS.system_task}
                  </>
                )}
                <span className="text-gray-400 text-xs">(不可更改)</span>
              </div>
            </div>
          )}

          {/* Basic info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">任务名称 *</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={
                  scheduleType === 'workflow'
                    ? '例如：每日用户头像生成'
                    : '例如：每日产物清理'
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="可选的任务描述..."
                rows={2}
              />
            </div>
          </div>

          {/* Cron expression */}
          <div className="space-y-4">
            <div className="p-4 border rounded-lg bg-gray-50 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cron 表达式 *
                </label>
                <Input
                  value={cronExpression}
                  onChange={(e) => setCronExpression(e.target.value)}
                  placeholder="0 10 * * 1-5"
                  className="font-mono"
                />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {CRON_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      onClick={() => setCronExpression(preset.value)}
                      className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                        cronExpression === preset.value
                          ? 'bg-blue-100 border-blue-300 text-blue-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  格式: 分 时 日 月 星期 (例如: 0 10 * * 1-5 = 每周一到周五 10:00)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">时区</label>
                <Select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  options={TIMEZONE_OPTIONS}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Cron 表达式按此时区计算触发时间。例如选择「亚洲/上海」+ 「0 10 * * *」= 北京时间每天 10:00 触发
                </p>
              </div>
            </div>
          </div>

          {/* Workflow-specific fields */}
          {scheduleType === 'workflow' && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                <Workflow className="h-4 w-4 text-blue-600" />
                工作流配置
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  工作流 *
                </label>
                {isEditing ? (
                  <Input value={workflowId} disabled />
                ) : (
                  <Select
                    value={workflowId}
                    onChange={(e) => setWorkflowId(e.target.value)}
                    options={[
                      { value: '', label: '选择工作流...' },
                      ...workflowOptions,
                    ]}
                  />
                )}
              </div>

              {/* Target User Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  目标用户
                </label>
                <WorkflowTargetSelector
                  value={target}
                  onChange={setTarget}
                  currentUserId={user?.user_id}
                />
              </div>

              {/* User Filter Options (only shown when all_valid_users is selected) */}
              {target.type === 'all_valid_users' && (
                <UserFilterOptionsForm
                  value={userFilter}
                  onChange={setUserFilter}
                />
              )}

              {/* Runtime Options */}
              <RuntimeOptionsForm
                value={runtimeOptions}
                onChange={setRuntimeOptions}
              />
            </div>
          )}

          {/* System task-specific fields */}
          {scheduleType === 'system_task' && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                <Settings className="h-4 w-4 text-purple-600" />
                系统任务配置
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  任务类型 *
                </label>
                {isEditing ? (
                  <Input value={taskType} disabled />
                ) : (
                  <Select
                    value={taskType}
                    onChange={(e) => setTaskType(e.target.value)}
                    options={[
                      { value: '', label: '选择任务类型...' },
                      ...SYSTEM_TASK_TYPE_OPTIONS,
                    ]}
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  任务配置 (JSON)
                </label>
                <Textarea
                  value={taskConfig}
                  onChange={(e) => setTaskConfig(e.target.value)}
                  placeholder='{"delete_files": true, "dry_run": false}'
                  rows={4}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  可选的任务配置参数，格式为 JSON
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !isFormValid()}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEditing ? '保存' : '创建'}
          </Button>
        </div>
      </div>
    </div>
  );
}
